<template>
  <!-- Duas formas da mesma coisa, e a escolha entre elas é a NATUREZA do valor: a bolinha é
       situação de fluxo, o fundo tingido é opção de domínio. Ver `ValueForm`.

       Sempre CHEIO: este componente desenha valor de escolha. Item de coleção é vazado, e se
       marca com a régua — quem o desenha são o `ColoredValue` e o `PropertyValue`. -->
  <span v-if="dot" class="option-dot-line" :title="option.name">
    <v-icon size="x-small" :color="option.color" class="option-dot">{{ VALUE_DOT }}</v-icon>{{ option.name }}
  </span>

  <!-- O fundo tingido ATRÁS DO NOME, e não uma caixa em volta dele.

       Era um `v-chip size="small"`, e o chip trazia junto o que não se queria: altura fixa de
       24px, que empurrava a linha de um campo no card e fazia "Prioridade" ocupar o dobro do
       vizinho; recuo de 12px, que num nome curto fazia a caixa parecer muito maior que o
       conteúdo; e um raio de pílula que destoava do resto da tela.

       `display: inline` é o ponto: o fundo pinta o texto e acompanha a linha, sem reservar
       altura própria. Sem `font-size` de propósito — o tamanho é o de quem hospeda, menor
       dentro do card e normal em toda outra tela. -->
  <span v-else class="option-chip-text" :class="tintClasses(option.color)"
        :title="option.name">{{ option.name }}</span>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {ColoredOption, VALUE_DOT} from '@/types/ColoredOption'
import {tintClasses} from '@/utils/colors'

/**
 * O valor de uma escolha, com a cor dele: uma opção de domínio, uma situação de fluxo, uma
 * raia do Kanban.
 *
 * Estava escrito em três lugares com três resultados parecidos — o chip de seleção do
 * `PropertyValue`, a bolinha do mesmo componente na variante de card, e a célula de situação
 * da listagem, desenhada à mão porque situação não é propriedade e não passava por ali.
 * Recebendo um `ColoredOption`, os três viram um: o contrato não pergunta de onde a cor veio.
 *
 * **Sem cor, sai neutro** (`tintClasses`). Acontece com o que ainda não tem id — item sendo criado — e
 * com o grupo "Não definido".
 *
 * **O CSS das duas formas é global** (`option-dot`, `option-chip-text`): a referência do markdown, que é
 * HTML e não componente, desenha com as mesmas classes (ver `markdownMentionView`).
 */
@Component({name: 'ontos-colored-option'})
class ColoredOptionChip extends Vue {
  @Prop({type: Object as () => ColoredOption, required: true}) readonly option!: ColoredOption
  /** A bolinha ao lado do nome, que é como se desenha a situação de um fluxo. */
  @Prop({type: Boolean, default: false}) readonly dot!: boolean
  //Expostos ao template: o que é de módulo não é enxergado de lá.
  VALUE_DOT = VALUE_DOT
  tintClasses = tintClasses
}

export default toNative(ColoredOptionChip)
</script>

<!-- Global, e não `scoped`: a referência do markdown usa as mesmas classes (ver o comentário da classe) -->
<style>
/* A bolinha acompanha a linha do texto em vez de flutuar acima dele.

   3px, e não os 8px do `left` do Vuetify: aquele é o respiro de um ícone de 24px ao lado de um
   rótulo de botão, e num ícone de 12px colado num nome deixava a bolinha solta, longe do texto
   a que pertence. */
.option-dot {
  margin-right: 3px;
  vertical-align: baseline;
}

/* O respiro à direita é do VALOR, e não de quem o põe na tela: enquanto houve uma barra entre
   os valores ninguém reparou na falta, e sem ela "Bug" e "Melhoria" encostavam um no outro.

   10px, os mesmos do `.pv-rel`: no card as duas formas aparecem uma embaixo da outra, e um
   respiro diferente em cada linha se lê como desalinho. O fundo tingido pede menos, porque o
   recuo dele já afasta o nome do vizinho. */
.option-dot-line {
  overflow-wrap: anywhere;
  margin-right: 10px;
}

/* `inline`, e não `inline-block`: caixa de bloco reserva altura própria e volta a empurrar a
   linha, que é justamente o que esta forma existe para evitar. Inline, o recuo lateral pinta o
   fundo sem mexer na altura — e um nome longo quebra junto com o texto em volta, em vez de ser
   cortado a seco como o chip fazia.

   Sem recuo vertical pelo mesmo motivo. Raio pequeno, e não de pílula: é um realce de texto.

   12px FIXOS, que é o tamanho da linha de campo do card — e não herdado de quem hospeda, como
   chegou a ser. Herdando, o mesmo valor saía a 12px dentro do card e a 16px na ficha e na
   listagem, e no tamanho do corpo o recuo em volta de um nome curto fazia um bloco: a cor
   deixava de ser um realce do texto e virava caixa. O valor de um domínio é uma etiqueta, e
   etiqueta não cresce com o texto ao redor. */
.option-chip-text {
  display: inline;
  padding: 0 6px;
  margin-right: 6px;
  border-radius: 4px;
  font-size: 12px;
}
</style>
